import { PurchaseType } from 'components/Hero/PDPHero/PurchaseControls';
import PDPHeroV2 from 'components/Hero/PDPHeroV2';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import Cookies from 'js-cookie';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import ewAnalytics from 'utils/analytics';
import { addToCart, addToCartDataLayer, resetSession } from 'utils/cartHelpers';
import { ANALYTICS } from 'utils/constants/analytics';
import { CookieNames } from 'utils/constants/cookies';
import { Promo } from 'utils/helpers/activePromoHelpers';
import { ProductInfoContext } from 'utils/helpers/pdpContexts';
import { useMembershipProducts } from 'utils/hooks';
import { UserContext } from 'utils/hooks/useUserContext/context';
import {
  CollectionMethodContentfulType,
  ImageType,
  Variant,
} from 'utils/types';
import { v4 as uuidv4 } from 'uuid';

import { PDPHero } from '../../../components/Hero';
import { DisclaimerDataProps } from '../../../components/PrePurchaseDisclaimer';
import { ScreenerDataProps } from '../../../components/PrePurchaseScreener';
import PrePurchaseWrapper from '../../../components/PrePurchaseWrapper';
import { UserSettingsProvider } from '../../../utils/hooks/useAccountSettings/context';
import microscope from './microscope.svg';
import stethoscope from './stethoscope.svg';

export type SubscriptionOptionType = {
  id: number;
  label: string;
  name: string;
  savingsStatement: string;
  percentDiscount: number;
  price: string;
};

export type HeroContainerProps = {
  consultAvailable?: boolean;
  consultAvailableText: string;
  descriptionHeader: string;
  descriptionText: string;
  measuresText: string;
  measuresIcon?: string;
  sliderImages: ImageType[];
  modalTitle?: string;
  modalButtonText?: string;
  listOfMeasuredItems?: {
    listOfContentSections: Array<{
      description: {
        description: string;
      };
      title: string;
      image: ImageType;
    }>;
  };
  hideSubscription?: boolean;
  preSelectedSubscription?: boolean;
  heroSubscriptionFaqUrl?: string;
  descriptionCallout?: string;
  ctaSubheadline: string;
  promo?: Promo;
  regionalRestrictionApplies?: boolean;
  isV2?: boolean;
  hasPrePurchaseStep?: boolean;
  disclaimer: DisclaimerDataProps | null;
  screener: ScreenerDataProps | null;
};

export const HeroContainer: React.FC<HeroContainerProps> = ({
  consultAvailable,
  consultAvailableText,
  descriptionHeader,
  descriptionText,
  measuresText,
  measuresIcon,
  sliderImages,
  modalButtonText,
  modalTitle,
  listOfMeasuredItems,
  hideSubscription,
  preSelectedSubscription,
  heroSubscriptionFaqUrl,
  descriptionCallout,
  ctaSubheadline,
  promo,
  regionalRestrictionApplies,
  isV2,
  hasPrePurchaseStep,
  disclaimer,
  screener,
}) => {
  const { userData } = useContext(UserContext);
  const productData = useContext(ProductInfoContext);

  const productWithMembership = useMembershipProducts(
    userData,
    productData.productsBySlug,
  );
  const {
    name,
    price,
    reviewData,
    collectionMethods,
    subscriptionVariants = [],
    variantId,
    productId,
    primaryCallouts,
    outOfStock,
    promotionable,
    maxQuantity,
  } = productData;

  const { membershipVariantId, memberPrice, memberHeroCopy, activeCredits } =
    productWithMembership[productData.slug] ?? {};

  let reviewCount = 0;
  let averageRating = 0;

  if (reviewData) {
    reviewCount = reviewData.review_count;
    averageRating = reviewData.average_rating;
  }
  const experimentCookie = Cookies.get(CookieNames.HomepageExperiment);

  const images = sliderImages.map((img) => ({
    image: (
      <Img
        loading="eager"
        image={img.gatsbyImageData}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          opacity: 1,
          transform: 'none',
          transition: 'none',
          objectFit: 'contain',
        }}
        imgStyle={{
          objectFit: 'contain',
          opacity: 1,
          transform: 'none',
          transition: 'none',
        }}
        alt={img.description}
      />
    ),
    imageAlt: img.description,
  }));

  const methods = collectionMethods.map(
    (method: CollectionMethodContentfulType) => ({
      name: method.v2Name ? method.v2Name : '',
      iconUrl: method.v2Icon ? method.v2Icon.file.url : '',
    }),
  );

  const measurements = [
    { name: measuresText, iconUrl: measuresIcon || microscope },
  ];

  const marketingCallout =
    primaryCallouts && primaryCallouts.length
      ? primaryCallouts[0].text
      : undefined;

  // Format subscription options
  const subscriptionOptionsByName = useMemo(
    () =>
      subscriptionVariants.reduce(
        (acc: { [key: string]: SubscriptionOptionType }, v: Variant) => {
          const {
            subscription_plan: subscriptionPlan,
            id,
            price: subscriptionPrice,
          } = v;
          const { name, percent_discount: percentDiscount } =
            subscriptionPlan || {};

          const savings = parseInt(price) - parseInt(subscriptionPrice);

          acc[String(name)] = {
            id,
            price: subscriptionPrice,
            label: `${name} Subscription: $${parseInt(
              subscriptionPrice,
            ).toFixed(0)}`,
            percentDiscount: percentDiscount ?? 0,
            name: String(name),
            savingsStatement: `YOU SAVE $${savings.toFixed(
              0,
            )} (${percentDiscount}%)`,
          };
          return acc;
        },
        {},
      ),
    [price, subscriptionVariants],
  );

  // Default subscription option was 2nd but now we have products with only one subscription option
  const defaultSubscriptionOption =
    Object.keys(subscriptionOptionsByName)[2] ??
    Object.keys(subscriptionOptionsByName)[0];

  const [activeSubscriptionOption, setActiveSubscriptionOption] =
    useState<SubscriptionOptionType>(
      subscriptionOptionsByName[defaultSubscriptionOption],
    );

  const handleSubscriptionOptionChange = (
    e: React.FormEvent<HTMLInputElement>,
  ) => {
    const target = e.target as HTMLInputElement;
    setActiveSubscriptionOption(subscriptionOptionsByName[target.name]);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CHANGED_FREQUENCY,
      data: { frequency: target.name },
    });
  };

  const [activePurchaseType, setActivePurchaseType] = useState<PurchaseType>(
    preSelectedSubscription ? 'subscribe' : 'oneTimePurchase',
  );

  useEffect(() => {
    // TODO: Remove this after the A/B test is over https://app.vwo.com/#/test/ab/179
    const campaignId = '179';
    const control = 'Control'; // Default to oneTimePurchase for control
    const variation = 'Variation'; // Default to subscribe for variation
    window.VWO?.push([
      'onVariationApplied',
      (data: any) => {
        if (!data) return;

        var expId = data[1];
        var variationId = data[2];

        var isExperimentDefined =
          typeof window._vwo_exp?.[expId].comb_n[variationId] != 'undefined';
        var isEventVisualExperiment =
          ['VISUAL_AB', 'VISUAL', 'SPLIT_URL', 'SURVEY'].indexOf(
            window._vwo_exp?.[expId].type,
          ) > -1;

        if (isExperimentDefined && isEventVisualExperiment) {
          var variant = window._vwo_exp?.[expId].comb_n[variationId];
          if (expId === campaignId) {
            if (variant === control) {
              setActivePurchaseType('oneTimePurchase');
            } else if (variant === variation) {
              setActivePurchaseType('subscribe');
            }
          }
        }
      },
    ]);
  }, []);

  const handlePurchaseTypeChange = (purchaseType: PurchaseType) => {
    setActivePurchaseType(purchaseType);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.PURCHASE_TYPE,
      data: { type: purchaseType, product: name, sku: productData.sku },
    });
  };

  const handleFrequencyTriggerClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.CHANGE_FREQUENCY_CLICKED,
      data: { product: name },
    });
  };

  const [quantity, setQuantity] = useState<number>(1);

  const handleExpressPayClick = (cb: () => void) => {
    resetSession();
    const eventId = uuidv4();
    if (activePurchaseType === 'subscribe') {
      addToCart(activeSubscriptionOption.id, undefined, cb, 1, eventId);
      addToCartDataLayer(activeSubscriptionOption.id, name, price, eventId);
    } else {
      addToCart(variantId, undefined, cb, quantity, eventId);
      addToCartDataLayer(variantId, name, price, eventId);
    }
  };

  /**
   * Add to cart click event handler
   * @param e React event
   * @param callback function to be triggered on click
   * @returns void
   */
  const handleAddToCartClick = (
    e: React.FormEvent<HTMLButtonElement>,
    callback?: () => void,
  ) => {
    const target = e.target as HTMLInputElement;
    const label =
      target.dataset.name === ANALYTICS.LABELS.ADD_TO_CART_MODAL
        ? ANALYTICS.LABELS.ADD_TO_CART_MODAL
        : ANALYTICS.LABELS.ADD_TO_CART;
    const sku = target.dataset.sku;
    ewAnalytics.track({
      data: {
        label,
        'Test ID': productId,
        'Test Name': name,
        'Purchase Type': activePurchaseType,
        Sku: sku ? sku : '',
        Experiment: experimentCookie ? experimentCookie : '',
      },
      event: ANALYTICS.EVENTS.ADD_TO_CART,
    });
    const eventId = uuidv4();
    if (activePurchaseType === 'subscribe') {
      addToCart(activeSubscriptionOption.id, undefined, callback, 1, eventId);
      addToCartDataLayer(activeSubscriptionOption.id, name, price, eventId);
    } else {
      addToCart(variantId, undefined, callback, quantity, eventId);
      addToCartDataLayer(variantId, name, price, eventId);
    }
  };

  const handleQuantityPickerChange = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setQuantity(+target.value);
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.QUANTITY_SELECT,
      data: {
        name,
        productId,
        quantity: target.value,
      },
    });
  };

  //The event is called on every slide change. Regardless of the source (click, swipe, etc)
  const handleSlide = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_SLIDE,
      data: { name, productId },
    });
  };

  const onSlideChangeFromSwipe = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_SWIPE,
      data: { name, productId },
    });
  };

  const onPrevArrowClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_PREV_ARROW_CLICK,
      data: { name, productId },
    });
  };

  const onNextArrowClick = () => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_NEXT_ARROW_CLICK,
      data: { name, productId },
    });
  };

  const onBulletClick = (bulletIndex: number) => {
    ewAnalytics.track({
      event: ANALYTICS.EVENTS.HERO_BULLET_CLICK,
      data: { name, productId, bulletIndex },
    });
  };

  const formattedMeasuredItems =
    listOfMeasuredItems?.listOfContentSections?.map((item) => {
      const formattedItemNames = item.description?.description.split('\n');
      return {
        image: item.image?.file?.url || '',
        title: item.title,
        items: formattedItemNames,
      };
    }) || [];

  const HeroComponent = isV2 ? PDPHeroV2 : PDPHero;

  return (
    <UserSettingsProvider>
      <PrePurchaseWrapper addToCart={handleAddToCartClick} />
      <HeroComponent
        productId={productId}
        name={name}
        descriptionHeader={descriptionHeader}
        description={descriptionText}
        price={price}
        memberPrice={memberPrice}
        memberHeroCopy={memberHeroCopy}
        membershipVariantId={membershipVariantId ?? 0}
        activeCredits={activeCredits}
        averageRating={averageRating}
        reviewCount={reviewCount}
        sliderImages={[...images]}
        collectionMethods={methods}
        consultAvailable={consultAvailable}
        consultAvailableText={consultAvailableText}
        consultIcon={stethoscope}
        measurements={measurements}
        subscriptionOptions={Object.values(subscriptionOptionsByName)}
        onSubscriptionOptionChange={handleSubscriptionOptionChange}
        activeSubscriptionOption={activeSubscriptionOption}
        activePurchaseType={activePurchaseType}
        onPurchaseTypeChange={handlePurchaseTypeChange}
        onAddToCartClick={handleAddToCartClick}
        onExpressPayClick={handleExpressPayClick}
        subscribeSelected={activePurchaseType === 'subscribe'}
        onQuantityPickerChange={handleQuantityPickerChange}
        quantity={quantity}
        topCallout={undefined}
        bottomCallout={marketingCallout}
        descriptionCallout={descriptionCallout}
        modalButtonText={modalButtonText}
        modalTitle={modalTitle}
        listOfMeasuredItems={formattedMeasuredItems}
        hideSubscription={subscriptionVariants.length === 0 || hideSubscription}
        heroSubscriptionFaqUrl={heroSubscriptionFaqUrl}
        onFrequencyTriggerClick={handleFrequencyTriggerClick}
        onSlide={handleSlide}
        ctaSubheadline={ctaSubheadline}
        isOutOfStock={Boolean(outOfStock)}
        promo={promo}
        promotionable={promotionable}
        onSlideChangeFromSwipe={onSlideChangeFromSwipe}
        onNextArrowClick={onNextArrowClick}
        maxQuantity={maxQuantity}
        onPrevArrowClick={onPrevArrowClick}
        onBulletClick={onBulletClick}
        regionalRestrictionApplies={regionalRestrictionApplies}
        hasPrePurchaseStep={hasPrePurchaseStep}
        disclaimer={disclaimer}
        screener={screener}
      />
    </UserSettingsProvider>
  );
};

export default HeroContainer;
